'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import ImagePlaceholder from '@/components/ui/ImagePlaceholder';
import Icon from '@/components/ui/AppIcon';
import Reveal from '@/components/common/Reveal';
import ProductGallery from './ProductGallery';
import ProductCard from './ProductCard';
import InquiryForm from './InquiryForm';
import {
  familyFinishes,
  hasFinishes,
  famFinishLine,
  finishLabel,
  finishCell,
  keyFeatures,
  careAndUse,
  certNote,
  specText,
  materialBadgeLabel,
  materialBadgeClass,
} from '@/lib/products/catalogueContent';
import type { Product, ProductSpec } from '../productsData';

const CATEGORY_ICON: Record<string, string> = {
  plates: 'PlateIcon',
  bowls: 'BowlIcon',
  trays: 'TrayIcon',
  cups: 'CupIcon',
  cutlery: 'CutleryIcon',
  custom: 'SparklesIcon',
};

interface Props {
  product: Product;
  related: Product[];
  /** Full catalogue  - passed through to the reused inquiry form (catalogue PDFs). */
  products: Product[];
  catalogueUrl: string | null;
}

// Read an initial ?size= index from the URL (pairs-with deep links), clamped.
function initialSize(count: number): number {
  if (typeof window === 'undefined') return 0;
  const n = Number(new URLSearchParams(window.location.search).get('size'));
  return Number.isInteger(n) && n >= 0 && n < count ? n : 0;
}

/** Render a "pairs with" value: linked segments when present, else the plain text. */
function PairsWith({ spec }: { spec: ProductSpec }) {
  if (spec.pl && spec.pl.length > 0) {
    return (
      <span>
        {spec.pl.map((g, i) =>
          g.family ? (
            <Link
              key={i}
              href={`/products/${g.family}${g.variantIndex ? `?size=${g.variantIndex}` : ''}`}
              className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            >
              {g.text}
            </Link>
          ) : (
            <span key={i}>{g.text}</span>
          )
        )}
      </span>
    );
  }
  return <span>{spec.pairs || '—'}</span>;
}

export default function ProductDetailInteractive({
  product,
  related,
  products,
  catalogueUrl,
}: Props) {
  const specs = product.specs ?? [];
  const [vi, setVi] = useState<number>(() => initialSize(specs.length));
  const [inquiryType, setInquiryType] = useState<string | null>(null);
  const inquiryRef = useRef<HTMLDivElement>(null);

  const requestInquiry = (type: string | null) => {
    setInquiryType(type);
    setTimeout(
      () => inquiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      100
    );
  };

  const gallery = product.images ?? [];
  const materialLabel = materialBadgeLabel(product);
  const breadcrumbMaterial = product.material === 'areca' ? 'Areca palm leaf' : 'Bagasse';
  const sel = specs[Math.min(vi, Math.max(specs.length - 1, 0))];
  const sizeCountLabel = `${specs.length} ${specs.length === 1 ? 'size' : 'sizes'}`;

  const finishes = familyFinishes(product);
  const features = keyFeatures(product);
  const care = careAndUse(product);

  const sectionHeading = 'font-headline text-2xl font-bold text-foreground md:text-3xl';

  return (
    <>
      <section className="px-4 pt-28 pb-16 sm:px-6 lg:px-8 lg:pt-32 lg:pb-20">
        <div className="mx-auto max-w-7xl">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 font-cta text-sm text-muted-foreground">
              <li>
                <Link href="/products" className="transition-colors hover:text-foreground">
                  Products
                </Link>
              </li>
              <li aria-hidden="true" className="text-border">
                <Icon name="ChevronRightIcon" size={14} />
              </li>
              <li>{breadcrumbMaterial}</li>
              <li aria-hidden="true" className="text-border">
                <Icon name="ChevronRightIcon" size={14} />
              </li>
              <li aria-current="page" className="font-medium text-foreground">
                {product.title}
              </li>
            </ol>
          </nav>

          <div className="grid gap-8 lg:grid-cols-2 lg:items-start lg:gap-12">
            {/* Media */}
            <Reveal className="overflow-hidden rounded-2xl border border-border bg-surface">
              {gallery.length > 0 ? (
                <ProductGallery key={product.id} images={gallery} alt={product.alt} />
              ) : (
                <div className="relative aspect-4/3 overflow-hidden">
                  <ImagePlaceholder
                    icon={CATEGORY_ICON[product.category ?? ''] ?? 'CubeIcon'}
                    label="Photo placeholder"
                    caption={product.title}
                  />
                </div>
              )}
            </Reveal>

            {/* Info */}
            <Reveal>
              {/* Badges */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 font-cta text-xs font-medium ${materialBadgeClass(product)}`}
                >
                  {product.materialLine || materialLabel}
                </span>
                <span className="inline-flex items-center rounded-full border border-border bg-surface px-3 py-1 font-cta text-xs text-muted-foreground">
                  {famFinishLine(product)}
                </span>
                <span className="inline-flex items-center rounded-full border border-border bg-surface px-3 py-1 font-cta text-xs text-muted-foreground">
                  {sizeCountLabel}
                </span>
              </div>

              <h1 className="mb-3 font-headline text-3xl font-bold leading-tight text-foreground md:text-4xl">
                {product.title}
              </h1>
              {product.description && (
                <p className="mb-6 font-body text-lg leading-relaxed text-muted-foreground">
                  {product.description}
                </p>
              )}

              {/* Available finishes (bagasse families with finishes) */}
              {hasFinishes(product) && (
                <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-border bg-muted/40 px-4 py-3">
                  <span className="font-cta text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Available finishes
                  </span>
                  {finishes.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center gap-2 font-body text-sm text-foreground"
                    >
                      <span
                        aria-hidden="true"
                        className="h-4 w-4 rounded-full border border-border"
                        style={{ background: f.swatch }}
                      />
                      {f.label} ({f.note.toLowerCase()})
                    </span>
                  ))}
                </div>
              )}

              {/* Choose a size */}
              {specs.length > 0 && (
                <div className="mb-6">
                  <h2 className="mb-3 font-cta text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Choose a size
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {specs.map((s, i) => (
                      <button
                        key={s.label}
                        type="button"
                        aria-pressed={i === vi}
                        onClick={() => setVi(i)}
                        className={`min-h-11 rounded-xl px-4 py-2 font-cta text-sm font-medium transition active:scale-[0.97] ${
                          i === vi
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'border border-border bg-surface text-foreground hover:border-primary/40'
                        }`}
                      >
                        {s.pill || s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected size panel */}
              {sel && (
                <div className="mb-6 rounded-2xl border border-border bg-surface p-5">
                  <p className="mb-4 font-headline text-lg font-bold text-foreground">
                    {sel.label}
                  </p>

                  {sel.dims.length > 0 && (
                    <div className="mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                      {sel.dims.map((d) => (
                        <div key={d.label} className="rounded-xl bg-muted/50 px-3.5 py-2.5">
                          <p className="font-body text-[11px] text-muted-foreground">{d.label}</p>
                          <p className="font-cta text-base font-bold text-foreground">{d.value}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 font-body text-sm">
                    {sel.util && (
                      <>
                        <dt className="text-muted-foreground">Utility</dt>
                        <dd className="text-foreground">{sel.util}</dd>
                      </>
                    )}
                    {sel.best && (
                      <>
                        <dt className="text-muted-foreground">Best for</dt>
                        <dd className="text-foreground">{sel.best}</dd>
                      </>
                    )}
                    <dt className="text-muted-foreground">Pairs with</dt>
                    <dd className="text-foreground">
                      <PairsWith spec={sel} />
                    </dd>
                    <dt className="text-muted-foreground">Finishes</dt>
                    <dd className="text-foreground">{finishLabel(sel.material)}</dd>
                    {sel.material && (
                      <>
                        <dt className="text-muted-foreground">Material</dt>
                        <dd className="text-foreground">{sel.material}</dd>
                      </>
                    )}
                  </dl>

                  {sel.tags && sel.tags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {sel.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-primary/10 px-3 py-1 font-cta text-xs text-primary"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={() => requestInquiry(null)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 font-cta text-sm font-semibold text-primary-foreground transition duration-200 hover:-translate-y-0.5 hover:bg-primary/90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Icon name="CubeIcon" size={18} aria-hidden="true" />
                Raise enquiry for this size
              </button>
            </Reveal>
          </div>

          {/* All sizes at a glance */}
          {specs.length > 1 && (
            <div className="mt-16 lg:mt-20">
              <h2 className={`mb-5 ${sectionHeading}`}>All sizes at a glance</h2>
              <div className="overflow-x-auto rounded-2xl border border-border bg-surface">
                <table className="w-full min-w-[840px] border-collapse text-left">
                  <thead>
                    <tr className="bg-primary font-cta text-sm text-primary-foreground">
                      <th className="px-5 py-3.5 font-semibold">Size</th>
                      <th className="px-5 py-3.5 font-semibold">Specifications</th>
                      <th className="px-5 py-3.5 font-semibold">Utility</th>
                      <th className="px-5 py-3.5 font-semibold">Best for</th>
                      <th className="px-5 py-3.5 font-semibold">Can be paired with</th>
                    </tr>
                  </thead>
                  <tbody className="font-body text-sm">
                    {specs.map((s, i) => (
                      <tr
                        key={s.label}
                        className={`border-t border-border align-top ${i === vi ? 'bg-muted/40' : ''}`}
                      >
                        <td className="px-5 py-3">
                          <button
                            type="button"
                            onClick={() => setVi(i)}
                            className="min-h-11 text-left font-cta font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                          >
                            {s.label}
                          </button>
                        </td>
                        <td className="px-5 py-3 text-muted-foreground">{specText(s) || '—'}</td>
                        <td className="px-5 py-3 text-foreground">{s.util || '—'}</td>
                        <td className="px-5 py-3 text-muted-foreground">{s.best || '—'}</td>
                        <td className="px-5 py-3 text-muted-foreground">
                          <PairsWith spec={s} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 font-body text-xs text-muted-foreground">
                Specifications are nominal; confirm with your quotation. Finishes:{' '}
                {finishCell(sel?.material)}.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Key features */}
      <section className="px-4 pb-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className={`mb-5 ${sectionHeading}`}>Key features</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.k} className="rounded-2xl border border-border bg-surface p-5">
                <span
                  aria-hidden="true"
                  className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  <Icon name="CheckCircleIcon" size={20} />
                </span>
                <p className="mb-1 font-headline text-base font-bold text-foreground">{f.k}</p>
                <p className="font-body text-sm leading-relaxed text-muted-foreground">{f.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Care and use */}
      <section className="px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className={`mb-5 ${sectionHeading}`}>Care and use</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {care.map((c) => (
              <div key={c.k} className="rounded-2xl border border-border bg-surface p-5">
                <span
                  aria-hidden="true"
                  className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  <Icon name="InformationCircleIcon" size={20} />
                </span>
                <p className="mb-1 font-headline text-base font-bold text-foreground">{c.k}</p>
                <p className="font-body text-sm leading-relaxed text-muted-foreground">{c.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certification and compliance */}
      <section className="px-4 pb-12 sm:px-6 lg:px-8 lg:pb-16">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-3xl border border-primary/10 bg-primary/5 p-6 sm:p-8">
            <h2 className={`mb-3 ${sectionHeading}`}>Certification and compliance</h2>
            <p className="max-w-3xl font-body text-base leading-relaxed text-foreground">
              Our products are manufactured to globally recognised standards for compostability,
              food-contact safety and quality, with documentation to support compliance for buyers
              in the UK, EU and USA.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {['Global standards', 'UK and EU compliance support', 'USA compliance support'].map(
                (t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 font-cta text-xs font-medium text-primary-foreground"
                  >
                    <Icon name="CheckBadgeIcon" size={14} aria-hidden="true" />
                    {t}
                  </span>
                )
              )}
            </div>
            <p className="mt-4 max-w-3xl font-body text-sm text-muted-foreground">
              {certNote(product)}Certification can vary by product, manufacturer and destination
              market. Details of the certificates, test reports and declarations available for your
              order are shared on enquiry.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-primary/10 pt-5">
              <span className="font-body text-sm text-foreground">
                Need certificates for your market?
              </span>
              <button
                type="button"
                onClick={() => requestInquiry(null)}
                className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-cta text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Icon name="DocumentTextIcon" size={16} aria-hidden="true" />
                Request certificate details
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* You may also need */}
      {related.length > 0 && (
        <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
          <div className="mx-auto max-w-7xl">
            <h2 className={`mb-6 ${sectionHeading}`}>You may also need</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r, i) => (
                <Reveal as="div" key={r.id} delay={(i % 4) * 60} className="h-full">
                  <ProductCard product={r} compact />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Enquiry form (reused; type set by the CTAs above) */}
      <div ref={inquiryRef}>
        <InquiryForm
          selectedInquiryType={inquiryType}
          products={products}
          catalogueUrl={catalogueUrl}
          prefillProduct={product}
        />
      </div>
    </>
  );
}
